/**
 * 業者ホーム（ダッシュボード）— 旧デモUI（docs/demo/contractor）の移植・実データ版。
 * グラデKPIカード（今月の手取り見込）＋新着案件・受注済みカード＋シタミーからのメッセージ。
 */
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { calcPayout } from "@/lib/fee";

interface Props {
  onGoJobs: () => void;
  onGoManage: () => void;
}

export function HomeDashboard({ onGoJobs, onGoManage }: Props) {
  const { data: cases } = useQuery({ queryKey: ["open-cases", "", "", "new"], queryFn: () => api.listOpenCases({}) });
  const { data: bids } = useQuery({ queryKey: ["my-bids"], queryFn: () => api.listMyBids() });

  const accepted = (bids ?? []).filter((b) => b.status === "accepted");
  const net = accepted.reduce((sum, b) => sum + calcPayout(b.amount), 0); // 手取り＝税込−手数料

  return (
    <div className="px-5 py-5 space-y-4">
      <div className="bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl p-5 text-white shadow-lg">
        <div className="text-xs opacity-80">受注の手取り見込（手数料控除後）</div>
        <div className="text-3xl font-black mt-1 tabular-nums">¥{net.toLocaleString("ja-JP")}</div>
        <div className="text-xs opacity-80 mt-1">受注 {accepted.length}件 ／ 応札 {(bids ?? []).length}件</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={onGoJobs} className="bg-white dark:bg-white/5 rounded-xl p-4 text-left border border-[var(--color-border)] active:scale-95 transition">
          <div className="text-xs text-ink-soft dark:text-white/60">募集中の案件</div>
          <div className="text-2xl font-black mt-1">{cases?.length ?? "—"}</div>
          <div className="text-xs text-brand-600 dark:text-brand-300 font-bold mt-1">今すぐ応札 →</div>
        </button>
        <button onClick={onGoManage} className="bg-white dark:bg-white/5 rounded-xl p-4 text-left border border-[var(--color-border)] active:scale-95 transition">
          <div className="text-xs text-ink-soft dark:text-white/60">受注済</div>
          <div className="text-2xl font-black mt-1">{accepted.length}</div>
          <div className="text-xs text-brand-600 dark:text-brand-300 font-bold mt-1">日程調整へ →</div>
        </button>
      </div>

      <div>
        <h3 className="text-xs text-ink-soft dark:text-white/60 font-bold mb-2 flex items-center gap-1">
          <Bell size={12} /> シタミーから業者さんへ
        </h3>
        <div className="bg-brand-50 dark:bg-brand-800/20 border border-brand-200 dark:border-brand-700 rounded-xl p-4">
          <div className="text-sm font-bold text-brand-900 dark:text-brand-100">下見ゼロで稼げます</div>
          <div className="text-xs text-brand-800 dark:text-brand-200 mt-1">写真と顧客入力の情報で、現地に行かずに見積もり。</div>
          <div className="mt-3 text-xs space-y-1 text-brand-800 dark:text-brand-200">
            <div>・プラットフォーム手数料 一律 20%</div>
            <div>・わかりやすい料率で、手取りがすぐ計算できます</div>
            <div>・レビュー連動で確実な入金（決済導入後）</div>
          </div>
        </div>
      </div>

      {/* ひえもんEC連動（2026-07-22）: 業者アカウントはひえもんと共通。機器・部材の仕入れ導線 */}
      <a
        href="https://hiemon.com"
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-white dark:bg-white/5 rounded-xl p-4 border border-[var(--color-border)] active:scale-95 transition"
      >
        <div className="text-sm font-bold">機器・部材の仕入れは「完涼！ひえもん」</div>
        <div className="text-xs text-ink-soft dark:text-white/60 mt-1">
          主要6メーカー456機種を型番検索してそのまま購入。シタミーの業者アカウントでそのままログインできます。
        </div>
        <div className="text-xs text-brand-600 dark:text-brand-300 font-bold mt-2">hiemon.com へ →</div>
      </a>
    </div>
  );
}
